import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * `Dialog` (UI r1 deliverable 7): a modal dialog or alert dialog built like
 * `CenterPeek`, not on Base UI's dialog. Focus goes inside on open, Tab
 * cycles inside, Esc and the backdrop close it through `onClose`, and focus
 * returns to where it was. It portals into the token root when there is a
 * `PortalContainer` and renders in place otherwise (never into the body,
 * which carries no tokens). It changes nothing outside itself and injects no
 * style element.
 */
import { useCallback, useEffect, useId, useRef, useState, } from 'react';
import { createPortal } from 'react-dom';
import { focusableIn, useFocusTrap, useHasLayerProvider, useLayer, usePortalContainer, useReturnFocus, } from './infra.js';
/** The kit's dialog classes, for a non-modal anchored panel that wants the same frame. */
export const dialogClass = {
    backdrop: 'cx-dialog__backdrop',
    root: 'cx-dialog cx-raised',
    header: 'cx-dialog__header',
    title: 'cx-dialog__title',
    description: 'cx-dialog__description',
    body: 'cx-dialog__body',
    content: 'cx-dialog__content',
    actions: 'cx-dialog__actions',
};
export function Dialog({ open, onClose, title, description, children, actions, variant = 'dialog', testId, initialFocus, busy = false, size = 'default', }) {
    const ref = useRef(null);
    const titleId = useId();
    const descriptionId = useId();
    const container = usePortalContainer();
    const hasProvider = useHasLayerProvider();
    const closeOnEsc = useCallback(() => {
        if (!busy)
            onClose('esc');
    }, [busy, onClose]);
    useReturnFocus(open);
    useLayer(open, closeOnEsc, 'dialog');
    const trap = useFocusTrap(ref);
    const [body, setBody] = useState(null);
    const scrolls = useScrolls(body);
    // `container` is a dependency: the token root's PortalContainer can arrive
    // after the first render, which moves the dialog into the portal.
    // biome-ignore lint/correctness/useExhaustiveDependencies: see above
    useEffect(() => {
        const root = ref.current;
        if (!open || !root)
            return;
        const target = initialFocus?.current ?? focusableIn(root)[0] ?? root;
        target.focus();
    }, [open, initialFocus, container]);
    if (!open)
        return null;
    const onKeyDown = (event) => {
        // With a provider, Esc arrives through the layer stack instead.
        if (event.key === 'Escape' && !hasProvider) {
            event.preventDefault();
            event.stopPropagation();
            closeOnEsc();
            return;
        }
        trap(event);
    };
    const onBackdrop = (event) => {
        if (event.target !== event.currentTarget || busy || variant === 'alert')
            return;
        onClose('backdrop');
    };
    const node = (_jsx("div", { className: dialogClass.backdrop, onClick: onBackdrop, children: _jsxs("div", { ref: ref, className: size === 'wide' ? `${dialogClass.root} cx-dialog--wide` : dialogClass.root, role: variant === 'alert' ? 'alertdialog' : 'dialog', "aria-modal": "true", "aria-labelledby": titleId, "aria-describedby": description ? descriptionId : undefined, "aria-busy": busy ? 'true' : undefined, "data-testid": testId, tabIndex: -1, onKeyDown: onKeyDown, children: [_jsxs("div", { className: dialogClass.header, children: [_jsx("h2", { className: dialogClass.title, id: titleId, children: title }), description && (_jsx("div", { className: dialogClass.description, id: descriptionId, children: description }))] }), children && (_jsx("div", { ref: setBody, className: dialogClass.body, tabIndex: scrolls ? 0 : undefined, children: _jsx("div", { className: dialogClass.content, children: children }) })), actions && _jsx("div", { className: dialogClass.actions, children: actions })] }) }));
    return container ? createPortal(node, container) : node;
}
/** Whether `el` scrolls; a scrolling body takes focus so the keyboard can scroll it. */
function useScrolls(el) {
    const [scrolls, setScrolls] = useState(false);
    useEffect(() => {
        if (!el)
            return;
        const measure = () => setScrolls(el.scrollHeight > el.clientHeight + 1);
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        if (el.firstElementChild)
            observer.observe(el.firstElementChild);
        return () => observer.disconnect();
    }, [el]);
    return scrolls;
}
//# sourceMappingURL=dialog.js.map